import { voidFn } from '@simplisafe/ewok'
import { type RefObject, useEffect, useState } from 'react'

/**
 * A hook that uses the Intersection Observer API to detect when an element enters or leaves the viewport
 * @param ref Reference to the component to observe
 * @param options IntersectionObserver options object containing root, rootMargin, and threshold
 * @returns The most recent IntersectionObserverEntry or null if the element is not being observed
 */
export const useIntersection = (
  ref: RefObject<HTMLElement>,
  options: IntersectionObserverInit
): IntersectionObserverEntry | null => {
  const [intersectionObserverEntry, setIntersectionObserverEntry] =
    useState<IntersectionObserverEntry | null>(null)

  useEffect(() => {
    if (ref.current && typeof IntersectionObserver === 'function') {
      const handler = (entries: readonly IntersectionObserverEntry[]) => {
        setIntersectionObserverEntry(entries[0] ?? null)
      }

      const observer = new IntersectionObserver(handler, options)
      observer.observe(ref.current)

      return () => {
        setIntersectionObserverEntry(null)
        observer.disconnect()
      }
    }
    return voidFn
  }, [ref.current, options.threshold, options.root, options.rootMargin])

  return intersectionObserverEntry
}
